import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { motion } from "framer-motion";
import styles from "./CardSettingsList.module.css";
import CardSettingsItem from "components/CardSettingsItem";

const getCardKey = (card, index = 0) =>
  String(
    card?.id ??
      card?.cardId ??
      card?.membershipCreditCardId ??
      `${card?.lastFour ?? "card"}-${card?.expireMonth ?? ""}-${
        card?.expireYear ?? ""
      }-${index}`
  );

const sortByPrimaryFirst = (list = []) =>
  [...list].sort((a, b) => (b.isPrimary ? 1 : 0) - (a.isPrimary ? 1 : 0));

const toSettingsCards = (list = []) =>
  sortByPrimaryFirst(
    list.map((card, i) => ({
      ...card,
      _cardKey: getCardKey(card, i),
      isDefault:
        card?.isDefault ?? card?.isSelected ?? card?.isPrimary ?? i === 0,
      isPrimary: card?.isPrimary ?? false,
    }))
  );

export function CardSettingsList({
  cardList,
  onCardUpdate,
  onEditCard,
  onDeleteCard,
}) {
  const [cards, setCards] = useState(toSettingsCards(cardList));

  useEffect(() => {
    setCards(toSettingsCards(cardList));
  }, [cardList]);

  const update = (updated, selectedCard = null) => {
    const sorted = sortByPrimaryFirst(updated);
    setCards(sorted);
    onCardUpdate?.(sorted, selectedCard);
  };

  const handleSetDefault = (targetCardOrEvent) => {
    const targetKey = targetCardOrEvent?._cardKey;
    if (!targetKey) return;
    const updatedCards = cards.map((card) => ({
      ...card,
      isDefault: card._cardKey === targetKey,
      isPrimary: card._cardKey === targetKey,
      isSelected: card._cardKey === targetKey,
      isChecked: card._cardKey === targetKey,
    }));
    const selectedCard =
      updatedCards.find((card) => card?._cardKey === targetKey) ?? null;
    update(updatedCards, selectedCard);
  };

  const handleTogglePrimary = (targetCard) => {
    const targetKey = targetCard._cardKey;
    const updatedCards = cards.map((card) => ({
      ...card,
      isPrimary: card._cardKey === targetKey,
      isDefault: card._cardKey === targetKey,
      isSelected: card._cardKey === targetKey,
      isChecked: card._cardKey === targetKey,
    }));
    const selectedCard =
      updatedCards.find((card) => card?._cardKey === targetKey) ?? null;
    update(updatedCards, selectedCard);
  };

  const handleEdit = async (targetCard) => {
    if (!onEditCard) return;
    await onEditCard(targetCard);
  };

  const handleDelete = async (targetCard) => {
    if (onDeleteCard) {
      try {
        await onDeleteCard(targetCard);
      } catch {
        return;
      }
    }
    update(cards.filter((card) => card._cardKey !== targetCard._cardKey));
  };

  return (
    <div className={styles.list}>
      {cards.map((card) => (
        <motion.div
          key={card._cardKey}
          layout="position"
          transition={{ duration: 0.35, ease: "easeInOut" }}
        >
          <CardSettingsItem
            cardInfo={card}
            isDefault={card.isDefault}
            isPrimary={card.isPrimary}
            onSetDefault={() => handleSetDefault(card)}
            onTogglePrimary={() => handleTogglePrimary(card)}
            onEdit={() => handleEdit(card)}
            onDelete={() => handleDelete(card)}
          />
        </motion.div>
      ))}
    </div>
  );
}

CardSettingsList.propTypes = {
  cardList: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      cardId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      lastFour: PropTypes.string.isRequired,
      nickName: PropTypes.string.isRequired,
      expireMonth: PropTypes.string.isRequired,
      expireYear: PropTypes.string.isRequired,
      zipCode: PropTypes.string,
    })
  ),
  onCardUpdate: PropTypes.func,
  onEditCard: PropTypes.func,
  onDeleteCard: PropTypes.func,
};

export default CardSettingsList;
